/**
 * Fingerprint of trip plan inputs. Used to detect when the plan is out of date
 * (inputs changed since last full recalc) without treating manual supply flow edits as stale.
 */

import { incompatibilityPairsFromRows, type TrailerCargoIncompatibilityPair } from './trailerCargoBan';

const FINGERPRINT_V2_PREFIX = 'v2:';

export type EventInputForFingerprint = {
    id: string;
    startDate?: number | string | null;
    endDate?: number | string | null;
    packingList: Array<{ itemTypeId: string; quantity: number; consumable?: boolean }>;
};

/**
 * Deterministic fingerprint from the inputs that affect the trip plan:
 * event order, source/dest, event dates, and packing list quantities per event.
 * Same inputs always produce the same fingerprint. Uses a djb2-style hash.
 */
export function computePlanInputsFingerprintSync(
    eventIdsInOrder: string[],
    sourceId: string,
    destId: string,
    eventsById: Map<string, EventInputForFingerprint>,
    trailerCargoIncompatibilityPairs?: TrailerCargoIncompatibilityPair[]
): string {
    const pairs = [...(trailerCargoIncompatibilityPairs ?? [])].sort((a, b) => {
        if (a.trailerId !== b.trailerId) return a.trailerId.localeCompare(b.trailerId);
        return a.cargoId.localeCompare(b.cargoId);
    });
    const events = eventIdsInOrder.map(id => {
        const ev = eventsById.get(id);
        if (!ev) return { id, startDate: null, endDate: null, packingList: [] };
        const packingList = [...ev.packingList].sort((a, b) => {
            if (a.itemTypeId !== b.itemTypeId) return a.itemTypeId.localeCompare(b.itemTypeId);
            if ((a.consumable ?? false) !== (b.consumable ?? false)) return (a.consumable ? 1 : 0) - (b.consumable ? 1 : 0);
            return (a.quantity ?? 0) - (b.quantity ?? 0);
        });
        return {
            id: ev.id,
            startDate: ev.startDate != null ? new Date(ev.startDate).toISOString() : null,
            endDate: ev.endDate != null ? new Date(ev.endDate).toISOString() : null,
            packingList,
        };
    });
    const payload = { eventIds: eventIdsInOrder, sourceId, destId, events, trailerCargoIncompatibilityPairs: pairs };
    const json = JSON.stringify(payload);
    let h = 5381;
    for (let i = 0; i < json.length; i++) {
        h = ((h << 5) + h) ^ json.charCodeAt(i);
    }
    return (h >>> 0).toString(16);
}

/**
 * Incompatibility pairs that matter for this trip: both trailer and cargo appear on some event packing list.
 * Changing a pair only affects fingerprints (and staleness) for trips that include both item types.
 */
export function relevantIncompatibilityPairsForTrip(
    trip: { events?: Array<{ packingListItems?: Array<{ itemType?: { id?: string } | null }> }> },
    allPairs: TrailerCargoIncompatibilityPair[]
): TrailerCargoIncompatibilityPair[] {
    const itemIds = new Set<string>();
    for (const e of trip.events ?? []) {
        for (const pl of e.packingListItems ?? []) {
            const id = pl.itemType?.id;
            if (id) itemIds.add(id);
        }
    }
    return allPairs.filter(p => itemIds.has(p.trailerId) && itemIds.has(p.cargoId));
}

/** Stored on `trips.planInputsFingerprint` after v2 (trip-scoped pairs + prefix). */
export function formatPlanInputsFingerprintStored(rawHexFromCompute: string): string {
    return `${FINGERPRINT_V2_PREFIX}${rawHexFromCompute}`;
}

export function isV2PlanInputsFingerprint(stored: string | null | undefined): boolean {
    return typeof stored === 'string' && stored.startsWith(FINGERPRINT_V2_PREFIX);
}

export type TripForStaleness = {
    planInputsFingerprint?: string | null;
    events?: Array<{
        id: string;
        startDate?: number | string | null;
        endDate?: number | string | null;
        packingListItems?: Array<{
            itemType?: { id: string; consumable?: boolean } | null;
            quantity?: number;
        }>;
    }>;
    sourceStorageLocation?: { id: string } | null;
    destStorageLocation?: { id: string } | null;
};

/**
 * Returns true if the trip has a stored fingerprint and current inputs differ from it
 * (plan should be recalculated). Manual supply flow edits do not change the fingerprint,
 * so they are not considered stale.
 * Pass global `trailerCargoIncompatibilities` from the same query as the trip so fingerprint
 * includes trailer–cargo rules **relevant to this trip** (both items on some packing list).
 */
export function isPlanStale(
    trip: TripForStaleness,
    trailerCargoIncompatibilities?: Array<{
        trailerItemType?: { id?: string } | null;
        cargoItemType?: { id?: string } | null;
    }> | null
): boolean {
    const stored = trip.planInputsFingerprint;
    if (!stored) return false;

    const sourceId = trip.sourceStorageLocation?.id ?? null;
    const destId = trip.destStorageLocation?.id ?? null;
    if (!sourceId || !destId) return false;

    const events = trip.events ?? [];
    const eventIdsInOrder = events.map(e => e.id);
    const eventsById = new Map<string, EventInputForFingerprint>();
    for (const ev of events) {
        const packingList = (() => {
            const out: Array<{ itemTypeId: string; quantity: number; consumable: boolean }> = [];
            for (const i of ev.packingListItems ?? []) {
                const it = i.itemType;
                if (!it?.id) continue;
                out.push({
                    itemTypeId: it.id,
                    quantity: i.quantity ?? 0,
                    consumable: it.consumable ?? false,
                });
            }
            return out;
        })();
        eventsById.set(ev.id, {
            id: ev.id,
            startDate: ev.startDate ?? null,
            endDate: ev.endDate ?? null,
            packingList,
        });
    }

    const allPairs = incompatibilityPairsFromRows(trailerCargoIncompatibilities ?? []);
    const relevantPairs = relevantIncompatibilityPairsForTrip(trip, allPairs);
    const currentV2Raw = computePlanInputsFingerprintSync(eventIdsInOrder, sourceId, destId, eventsById, relevantPairs);
    const currentV2 = formatPlanInputsFingerprintStored(currentV2Raw);

    if (isV2PlanInputsFingerprint(stored)) {
        return currentV2 !== stored;
    }
    const legacyCurrent = computePlanInputsFingerprintSync(eventIdsInOrder, sourceId, destId, eventsById, allPairs);
    return legacyCurrent !== stored;
}
